import { Component, OnInit, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RequestTask, RequestsResponse, PaginationInfo } from '../../../shared/services/request-management.service';

/**
 * Base component for request list views
 * Provides shared pagination, loading, and data management logic
 */
@Component({
  template: ''
})
export abstract class BaseRequestListComponent implements OnInit, OnDestroy {
  protected destroy$ = new Subject<void>();

  // Observable streams
  protected requestsSubject$ = new BehaviorSubject<RequestTask[]>([]);
  protected loadingSubject$ = new BehaviorSubject<boolean>(false);
  protected totalRecordsSubject$ = new BehaviorSubject<number>(0);
  protected errorSubject$ = new BehaviorSubject<string | null>(null);

  public requests$: Observable<RequestTask[]> = this.requestsSubject$.asObservable();
  public loading$: Observable<boolean> = this.loadingSubject$.asObservable();
  public totalRecords$: Observable<number> = this.totalRecordsSubject$.asObservable();
  public error$: Observable<string | null> = this.errorSubject$.asObservable();

  // Pagination state
  public currentPage = 0;
  public pageSize = 10;
  public pageSizeOptions = [10, 25, 100];

  /**
   * Change page size and reset to first page
   */
  changePageSize(newPageSize: number): void {
    this.pageSize = newPageSize;
    this.currentPage = 0;
    this.fetchRequests(this.currentPage);
  }

  /**
   * Abstract method that child components must implement
   * Defines how to fetch requests for this specific tab
   */
  abstract fetchRequests(pageNumber: number, delegationId?: number): void;

  ngOnInit(): void {
    this.fetchRequests(this.currentPage);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle pagination - called from template
   */
  loadPage(pageNumber: number): void {
    this.currentPage = pageNumber;
    this.fetchRequests(pageNumber);
  }

  /**
   * Update requests data from response
   */
  protected updateRequests(response: RequestsResponse): void {
    this.requestsSubject$.next(response.data);
    this.totalRecordsSubject$.next(response.totalRecords);
    this.errorSubject$.next(null);
  }

  /**
   * Handle error during API call
   */
  protected handleError(error: any): void {
    console.error('Error fetching requests:', error);
    this.errorSubject$.next('Failed to load requests. Please try again.');
    this.loadingSubject$.next(false);
  }

  /**
   * Build pagination info object
   */
  protected buildPaginationInfo(pageNumber: number): PaginationInfo {
    return {
      pageSize: this.pageSize,
      pageNumber: pageNumber
    };
  }

  /**
   * Calculate total pages
   */
  getTotalPages(): number {
    return Math.ceil(this.totalRecordsSubject$.value / this.pageSize);
  }

  /**
   * Check if there are more pages
   */
  hasNextPage(): boolean {
    return (this.currentPage + 1) < this.getTotalPages();
  }

  /**
   * Check if there is a previous page
   */
  hasPreviousPage(): boolean {
    return this.currentPage > 0;
  }

  /**
   * Get array of page numbers for pagination display
   */
  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const maxDisplayPages = 5;
    const pages: number[] = [];

    if (totalPages <= maxDisplayPages) {
      // Show all pages if less than max
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, last, and pages around current page
      const startPage = Math.max(0, this.currentPage - 2);
      const endPage = Math.min(totalPages - 1, this.currentPage + 2);

      // Add first page
      if (startPage > 0) {
        pages.push(0);
      }

      // Add ellipsis if needed
      if (startPage > 1) {
        pages.push(-1); // -1 represents ellipsis
      }

      // Add pages around current
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (endPage < totalPages - 2) {
        pages.push(-1); // -1 represents ellipsis
      }

      // Add last page
      if (endPage < totalPages - 1) {
        pages.push(totalPages - 1);
      }
    }

    return pages;
  }
}
